import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import PlusIconModern from "@ecologi/assets/icons/NumberField/plus_white.svg";
import MinusIconModern from "@ecologi/assets/icons/NumberField/minus_white.svg";
import PlusIconBasic from "@ecologi/assets/icons/plus-basic-icon.svg";
import MinusIconBasic from "@ecologi/assets/icons/minus-icon.svg";

const Root = styled.div<{ variant: Variant }>`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${(props) =>
    props.variant === "basic" &&
    css`
      align-items: flex-start;
      border: 1px solid ${props.theme.colors.charcoal};
      max-width: 140px;
      height: 40px;
    `}
`;

const Input = styled.input<{ variant: Variant }>`
  -moz-appearance: textfield;
  appearance: textfield;
  margin: 0;
  border: none;
  padding: 0;
  width: 64px;
  height: 36px;
  font-size: 32px;
  font-weight: bold;
  text-align: center;
  color: ${(props) => props.theme.colors.night};
  background-color: transparent;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
  }

  ${(props) =>
    props.variant === "basic" &&
    css`
      font-size: 16px;
      font-weight: 400;
    `}
`;

const Button = styled.button<{ active: boolean; variant: Variant }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${
    ({ active, theme }) =>
      active ? theme.colors.forest : "#d7d7d7" /* mistDarker */
  };
  &:focus-visible {
    outline: ${(props) => `2px solid ${props.theme.colors.forest}`};
  }
  border: none;
  flex-shrink: 0;
  padding: 0;
  cursor: ${({ active }) => (active ? "pointer" : "not-allowed")};
  ${(props) =>
    props.variant === "basic" &&
    css`
      background-color: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
    `}
`;

const createEvent = (value: number) => ({ target: { value } });

type Variant = "basic" | "modern";

type NumberFieldEvent = {
  target: {
    value: number;
  };
};

type InputProps = {
  className?: string;
  label: string;
  max?: number;
  min?: number;
  name: string;
  onChange: (e: NumberFieldEvent) => void;
  value: number;
  readOnly?: boolean;
  variant?: Variant;
  onBoundsError?: (type: "above" | "below") => void;
};

export const NumberField = ({
  label,
  max = 100,
  min = 0,
  name,
  onChange,
  onBoundsError = () => {},
  value,
  readOnly = true,
  variant = "modern",
  ...rootProps
}: InputProps) => {
  const [inputValue, setInputValue] = useState<string | number>(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleDecrement = () => {
    if (value > min) {
      onChange(createEvent(Number(value) - 1));
    }
  };

  const handleIncrement = () => {
    if (value < max) {
      onChange(createEvent(Number(value) + 1));
    }
  };

  const handleChange = (value: number) => {
    if (value > max) {
      setInputValue(max);
      onBoundsError("above");
      return onChange(createEvent(Number(max)));
    }

    if (value < min) {
      setInputValue(min);
      onBoundsError("below");
      return onChange(createEvent(Number(min)));
    }

    onChange(createEvent(Number(value)));
  };

  const MinusIcon = variant === "modern" ? MinusIconModern : MinusIconBasic;
  const PlusIcon = variant === "modern" ? PlusIconModern : PlusIconBasic;

  return (
    <Root variant={variant} {...rootProps}>
      <Button
        variant={variant}
        type="button"
        onClick={handleDecrement}
        active={value > min}
        data-testid="number-field-minus"
      >
        <MinusIcon />
      </Button>
      <Input
        variant={variant}
        type="number"
        name={name}
        aria-label={label}
        min={min}
        onKeyPress={(event) => {
          const keyCode = event.keyCode || event.which;
          const keyValue = String.fromCharCode(keyCode);
          if (/\+|-|\./.test(keyValue)) event.preventDefault();
        }}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={(e) => handleChange(Number(e.target.value))}
        max={max}
        value={inputValue}
        readOnly={readOnly}
      />
      <Button
        variant={variant}
        type="button"
        onClick={handleIncrement}
        active={value < max}
        data-testid="number-field-plus"
      >
        <PlusIcon />
      </Button>
    </Root>
  );
};
